import PetService from '@/api/services/PetService';

export const namespaced = true;

export const state = {
  pets: [],
  breeds: [],
  breedsCat: [],
  breedsDog: [],
  fetchBreeds: {
    error: null,
    isSuccess: false,
    isLoading: false,
  },
  fetchPets: {
    error: null,
    isSuccess: false,
    isLoading: false,
  },
  error: null,
  isSuccess: false,
  isLoading: false,
};

export const getters = {
  getAllActivePetNames: (state) => state.pets?.filter((pet) => pet.status === 'A').map((pet) => pet.name || '') || [],
  getAllInactivePetNames: (state) => state.pets?.filter((pet) => pet.status !== 'A').map((pet) => pet.name || '') || [],
  getError: ({ error }) => error,
};

export const mutations = {
  //  FETCH PET
  SET_FETCH_PETS_SUCCESS(state, allPets) {
    state.fetchPets.error = null;
    state.pets = allPets || [];
  },
  SET_FETCH_PETS_FAILURE(state, error) {
    state.fetchPets.error = error;
  },
  SET_FETCH_PETS_LOADING_ON(state) {
    state.fetchPets.isLoading = true;
  },
  SET_FETCH_PETS_LOADING_OFF(state) {
    state.fetchPets.isLoading = false;
  },
  //   ADD PET
  SET_ADD_PET_SUCCESS(state) {
    state.error = null;
  },
  SET_ADD_PET_FAILURE(state, error) {
    state.error = error;
  },
  SET_ADD_PET_LOADING_ON(state) {
    state.isLoading = true;
  },
  SET_ADD_PET_LOADING_OFF(state) {
    state.isLoading = false;
  },
  //  BREEDS
  ADD_BREEDS(state, breeds) {
    state.breeds = breeds.map((breed) => (breed.includes('|') ? breed.substring(2) : breed));
    state.breedsCat = breeds.filter((breed) => breed.startsWith('C|')).map((breed) => breed.substring(2));
    state.breedsDog = breeds.filter((breed) => breed.startsWith('D|')).map((breed) => breed.substring(2));
  },
  SET_FETCH_BREEDS_SUCCESS({ fetchBreeds }) {
    fetchBreeds.error = null;
  },
  SET_FETCH_BREEDS_FAILURE({ fetchBreeds }, error) {
    fetchBreeds.error = error;
  },
  SET_FETCH_BREEDS_LOADING_ON({ fetchBreeds }) {
    fetchBreeds.isLoading = true;
  },
  SET_FETCH_BREEDS_LOADING_OFF({ fetchBreeds }) {
    fetchBreeds.isLoading = false;
  },
  clearError(state) {
    state.error = null;
  },
};

export const actions = {
  fetchPets: ({ commit }) => {
    commit('SET_FETCH_PETS_LOADING_ON');
    PetService.fetchPets()
      .then(({ allPets }) => commit('SET_FETCH_PETS_SUCCESS', allPets))
      .catch(({ error }) => commit('SET_FETCH_PETS_FAILURE', error))
      .finally(() => commit('SET_FETCH_PETS_LOADING_OFF'));
  },
  fetchBreeds: ({ commit }) => {
    commit('SET_FETCH_BREEDS_LOADING_ON');
    PetService.getBreeds()
      .then((breeds) => {
        commit('ADD_BREEDS', breeds);
        commit('SET_FETCH_BREEDS_SUCCESS');
      })
      .catch(({ error }) => {
        commit('ADD_BREEDS', FALLBACK_BREEDS);
        commit('SET_FETCH_BREEDS_FAILURE', error);
      })
      .finally(() => commit('SET_FETCH_BREEDS_LOADING_OFF'));
  },
  async addPet({ commit, dispatch }, { payload }) {
    try {
      commit('SET_ADD_PET_LOADING_ON');

      const pet = await PetService.addPet(payload);

      commit('SET_ADD_PET_SUCCESS');

      await dispatch('notification/notifySuccess', { message: `${payload.name} has been added to your account.` }, { root: true });

      return pet;
    } catch (error) {
      commit('SET_ADD_PET_FAILURE', error?.message);

      await dispatch(
        'notification/notifyFailure',
        {
          message: 'Failed to add a new pet.',
          options: { dismissSecs: 15 },
        },
        { root: true }
      );

      throw error;
    } finally {
      commit('SET_ADD_PET_LOADING_OFF');
    }
  },
  clearErrors(store) {
    store.commit('clearError');
  },
};

const FALLBACK_BREEDS = [
  'Affenpinscher',
  'Afghan Hound',
  'Airedale Terrier',
  'Akita',
  'Alaskan Malamute',
  'American Eskimo Dog (Miniature)',
  'American Eskimo Dog (Standard)',
  'American Eskimo Dog (Toy)',
  'American Staffordshire Terrier',
  'Anatolian Shepherd Dog',
  'Australian Cattle Dog',
  'Australian Kelpie',
  'Australian Shepherd',
  'Australian Stumpy Tail Cattle Dog',
  'Australian Terrier',
  'Barbet',
  'Basenji',
  'Basset hound',
  'Beagle',
  'Bearded Collie',
  'Beauceron',
  'Bedlington Terrer',
  'Belgian Shepherd',
  'Berger des Pyrenees',
  'Berger Picard',
  'Bernese Mountain Dog',
  'Bichon Frisé',
  'Black Russian Terrier',
  'Bloodhound',
  'Border Collie',
  'Border Terrier',
  'Borzoï',
  'Boston Terrier',
  'Bouvier des Flandres',
  'Boxer',
  'Braque Français (Gascogne)',
  'Braque Français (Pyrenees)',
  'Briard',
  'Bull Terrier',
  'Bull Terrier (Miniature)',
  'Bullmastiff',
  'Cairn Terrier',
  'Canaan Dog',
  'Canadian Eskimo Dog',
  'Cane Corso',
  'Cavalier King Charles Spaniel',
  'Cesky Terrier',
  'Chihuahua (Long Coat)',
  'Chihuahua (Short Coat)',
  'Chinese Crested Dog',
  'Chinese Shar-Pei',
  'Chow Chow',
  'Collie (Rough)',
  'Collie (Smooth)',
  'Coonhound (Black and Tan)',
  'Corgi (Cardigan)',
  'Corgi (Pembroke)',
  'Coton de Tulear',
  'Dachshund (Miniature Long)',
  'Dachshund (Miniature Smooth)',
  'Dachshund (Miniature Wire-Hair)',
  'Dachshund (Standard Long)',
  'Dachshund (Standard Smooth)',
  'Dachshund (Standard Wire-Hair)',
  'Dalmatian',
  'Dandie Dinmont Terrier',
  'Doberman Pinscher',
  'Dogue de Bordeaux',
  'Drever',
  'English Bulldog',
  'English Toy Spaniel',
  'Entlebucher Mountain Dog',
  'Eurasier',
  'Finnish Lapphund',
  'Finnish Spitz',
  'Fox Terrier (Smooth)',
  'Fox Terrier (Wire)',
  'Foxhound (American)',
  'Foxhound (English)',
  'French Bulldog',
  'German Pinscher',
  'German Shepherd Dog',
  'Giant Schnauzer',
  'Glen of Imaal',
  'Great Dane',
  'Great Pyrenees',
  'Greater Swiss Mountain Dog',
  'Greyhound',
  'Griffon (Brussels)',
  'Griffon (Wire-Haired Pointing)',
  'Harrier',
  'Havanese',
  'Hovawart',
  'Ibizan Hound',
  'Iceland Sheepdog',
  'Irish Terrier',
  'Irish Wolfhound',
  'Italian Greyhound',
  'Japanese Chin',
  'Japanese Spitz',
  'Karelian Bear Dog',
  'Keeshond',
  'Kerry Blue Terrier',
  'Kleiner Münsterländer',
  'Komondor',
  'Kuvasz',
  'Lagotto romagnolo',
  'Lakeland Terrier',
  'Lancashire Heeler',
  'Leonberger',
  'Lhaso Apso',
  'Lowchen',
  'Maltese',
  'Manchester Terrier',
  'Manchester Terrier',
  'Mastiff',
  'Miniature Pinscher',
  'Miniature Schnauzer',
  'Mudi',
  'Neopolitan Mastiff',
  'Norfolk Terrier',
  'Norrbottenspets',
  'Norwegian Buhund',
  'Norwegian Elkhound',
  'Norwegian Lundehund',
  'Norwich Terrier',
  'Old English Sheepdog',
  'Otterhound',
  'Papillion',
  'Parson Russell Terrier',
  'Pekingese',
  'Petit basset griffon vendéen',
  'Pharaoh hound',
  'Pointer',
  'Pointer (German Long-Haired)',
  'Pointer (German Short-Haired)',
  'Pointer (German Wire-Haired)',
  'Polish Lowland Sheepdog',
  'Pomeranian',
  'Poodle (Miniature)',
  'Poodle (Standard)',
  'Poodle (Toy)',
  'Portuguese Sheepdog',
  'Portuguese Water Dog',
  'Pudelpointer',
  'Pug',
  'Puli',
  'Rat Terrier',
  'Retriever (Chesapeake Bay)',
  'Retriever (Curly-Coated)',
  'Retriever (Flat-Coated)',
  'Retriever (Golden)',
  'Retriever (Labrador)',
  'Retriever (Nova Scotia Duck Tolling)',
  'Rhodesian ridgeback',
  'Rottweiler',
  'Saint-Bernard',
  'Samoyed',
  'Schapendoes',
  'Schipperke',
  'Scottish Deerhound',
  'Scottish Terrier',
  'Sealyham Terrier',
  'Setter (English)',
  'Setter (Gordon)',
  'Setter (Irish Red and White)',
  'Setter (Irish)',
  'Shetland Sheepdog',
  'Shiba Inu',
  'Shih Tzu',
  'Shikoku',
  'Siberian Husky',
  'Silky Terrier',
  'Skye Terrier',
  'Soft-Coated Wheaten Terrier',
  'Spaniel (American Cocker)',
  'Spaniel (American Water)',
  'Spaniel (Blue Picardy)',
  'Spaniel (Brittany)',
  'Spaniel (Clumber)',
  'Spaniel (English Cocker)',
  'Spaniel (English Springer)',
  'Spaniel (Field)',
  'Spaniel (French)',
  'Spaniel (Irish Water)',
  'Spaniel (Sussex)',
  'Spaniel (Welsh Springer)',
  'Spanish Water Dog',
  'Spinone Italiano',
  'Staffordshire Terrier',
  'Standard Schnauzer',
  'Swedish Vallhund',
  'Tatra Sheepdog',
  'Tibetan Mastiff',
  'Tibetan Terrier',
  'Toy Fox Terrier',
  'Vizsla (Smooth)',
  'Vizsla (Wire-Haired)',
  'Weimaraner',
  'Welsh Terrier',
  'West Highland White Terrier',
  'Whippet',
  'Xoloitcuintli (Toy)',
  'Xoloitzcuintli (miniature)',
  'Xoloitzcuintli (Standard)',
  'Yorkshire Terrier',
  'British Shorthair',
  'Domestic Shorthair',
  'Domestic Longhair',
  'Abyssinian',
  'American Wirehair',
  'American Shorthair',
  'American Curl',
  'Turkish Angora',
  'Balinese',
  'Bengal',
  'Russian Blue',
  'Japanese Bobtail',
  'Bombay',
  'Havana Brown',
  'Burmese',
  'Foreign Burmese',
  'Burmilla',
  'Chantilly',
  'Chartreux',
  'Manx',
  'Birman (Sacred Cat of Burma)',
  'Norwegian Forest Cat',
  'Colourpoint Shorthair',
  'Cymric',
  'Devon Rex',
  'Exotic',
  'Foldex',
  'Himalayan',
  'Non-Pointed Himalayan',
  'Korat',
  'Maine Coon',
  'Egyptian Mau',
  'Ocicat',
  'Oriental',
  'Persian',
  'Pixiebob',
  'Ragamuffin',
  'Ragdoll',
  'Cornish Rex',
  'Savannah',
  'Scottish Fold',
  'Selkirk Rex',
  'Siamese',
  'Siberian',
  'Singapura',
  'Sokoke',
  'Somali',
  'Sphynx',
  'Tonkinese',
];
